import styled from 'styled-components';

const TEXT_PRIMARY = '#040404';
const TEXT_SECONDARY = '#464646';
const ACCENT_TEAL = '#3a6767';
const LINK_TEAL = '#315454';
const TILE_BORDER = '#bdbdbd';
const SURFACE = '#f4f9f8';
const PRIMARY_CTA_FILL = '#2b4344';

const trialButtonBase = `
    box-sizing: border-box;
    margin: 0;
    padding: 12px 20px;
    border-radius: 8px;
    font-family: 'Objektiv Mk2 Bold', 'Objektiv Mk2', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;
    cursor: pointer;
    flex-shrink: 0;
    min-height: 44px;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT_TEAL};
        outline-offset: 2px;
    }
`;

export const WelcomeContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    max-width: 100%;
    text-align: left;

    @media (max-width: 815px) {
        gap: 20px;
    }
`;

export const IntroBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

export const WelcomeTitle = styled.h2`
    margin: 0;
    font-family: 'Objektiv Mk2 Bold', 'Objektiv Mk2', sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 32px;
    color: ${TEXT_PRIMARY};
    text-transform: none;
`;

export const WelcomeIntro = styled.p`
    margin: 0;
    font-family: 'Objektiv Mk2', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: ${TEXT_SECONDARY};
`;

export const TrialAccent = styled.span`
    font-family: 'Objektiv Mk2', sans-serif;
    font-weight: 500;
    color: ${ACCENT_TEAL};
`;

export const FeatureGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    width: 100%;

    @media (max-width: 815px) {
        grid-template-columns: 1fr;
        gap: 8px;
    }
`;

export const FeatureTile = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 16px;
    background: #fff;
    border: 1px solid ${TILE_BORDER};
    border-radius: 8px;
    min-width: 0;

    @media (max-width: 815px) {
        align-items: center;
        gap: 4px;
        padding: 12px;
    }
`;

export const FeatureIconCircle = styled.div`
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: ${SURFACE};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ $isAiProductivity }) => ($isAiProductivity ? ACCENT_TEAL : TEXT_PRIMARY)};

    svg {
        display: block;
        flex-shrink: 0;
    }
`;

export const FeatureCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
`;

export const FeatureTitle = styled.p`
    margin: 0;
    font-family: 'Objektiv Mk2', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    color: ${TEXT_PRIMARY};
`;

export const FeatureDescription = styled.p`
    margin: 0;
    font-family: 'Objektiv Mk2', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    color: ${TEXT_SECONDARY};
`;

export const TrialError = styled.p`
    margin: 0;
    color: #de1717;
    font-family: 'Objektiv Mk2', sans-serif;
    font-size: 14px;
    line-height: 20px;
`;

export const TrialFooterActions = styled.div`
    display: flex;
    box-sizing: border-box;
    width: 100%;

    &.premiumTrialWelcomeDesktopFooter {
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        width: 100%;
        margin: 0;
        box-sizing: border-box;

        @media (max-width: 815px) {
            display: none;
        }
    }

    &.premiumTrialWelcomeMobileFooter {
        display: none;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: 16px;
        margin: 0;
        padding: 8px 0 calc(24px + env(safe-area-inset-bottom, 0px));

        @media (max-width: 815px) {
            display: flex;
        }
    }
`;

export const PickPlanButton = styled.button`
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    font-family: 'Objektiv Mk2 Bold', 'Objektiv Mk2', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: ${LINK_TEAL};
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        color: #2e5252;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT_TEAL};
        outline-offset: 2px;
    }

    @media (max-width: 815px) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        text-align: center;
        white-space: nowrap;

        @media (max-width: 390px) {
            font-size: 12px;
            letter-spacing: -0.2px;
        }
    }
`;

export const ContinueTrialButton = styled.button`
    ${trialButtonBase}
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid ${PRIMARY_CTA_FILL};
    background: ${PRIMARY_CTA_FILL};
    color: #fff;
    width: auto;
    min-width: 0;

    &:hover:not(:disabled) {
        background: #234a4c;
        border-color: #234a4c;
    }

    @media (max-width: 815px) {
        width: 100%;
    }
`;

export const ContinueArrowIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;

    @media (max-width: 815px) {
        display: none;
    }
`;
